import { motion } from "framer-motion";
import { Sparkles, Coins, Trophy } from "lucide-react";
import { scrollTo } from "./Header";
import { useLang, STR } from "@/i18n";

const U = (id) => `https://images.unsplash.com/${id}?fm=jpg&q=80&w=1200&auto=format&fit=crop`;

const PRODUCTS = [
  { id: "iphone", credits: 60000, names: { pt: "iPhone", en: "iPhone", es: "iPhone" }, tone: "gold", img: U("photo-1758186351359-66b2b268f0df"), featured: true },
  { id: "nb-gamer", credits: 48000, names: { pt: "Notebook Gamer", en: "Gaming Laptop", es: "Notebook Gamer" }, tone: "blue", img: U("photo-1603302576837-37561b2e2302") },
  { id: "bike", credits: 48000, names: { pt: "Bicicleta Elétrica", en: "Electric Bike", es: "Bicicleta Eléctrica" }, tone: "violet", img: U("photo-1624243519828-52a0f2c88af3") },
  { id: "ps5", credits: 24000, names: { pt: "PlayStation 5", en: "PlayStation 5", es: "PlayStation 5" }, tone: "gold", img: U("photo-1622297845775-5ff3fef71d13") },
  { id: "xbox", credits: 24000, names: { pt: "Xbox Series X", en: "Xbox Series X", es: "Xbox Series X" }, tone: "blue", img: U("photo-1621259182978-fbf93132d53d") },
  { id: "tv", credits: 20000, names: { pt: 'Smart TV 55" 4K', en: '55" 4K Smart TV', es: 'Smart TV 55" 4K' }, tone: "violet", img: U("photo-1593359677879-a4bb92f829d1") },
  { id: "phone", credits: 16000, names: { pt: "Smartphone Premium", en: "Premium Smartphone", es: "Smartphone Premium" }, tone: "gold", img: U("photo-1762512949121-c1fc05b36e1a") },
  { id: "notebook", credits: 14000, names: { pt: "Notebook", en: "Laptop", es: "Notebook" }, tone: "blue", img: U("photo-1611078489935-0cb964de46d6") },
  { id: "tablet", credits: 10000, names: { pt: "Tablet", en: "Tablet", es: "Tablet" }, tone: "violet", img: U("photo-1623934199716-dc28818a6ec7") },
  { id: "robo", credits: 8000, names: { pt: "Robô Aspirador", en: "Robot Vacuum", es: "Robot Aspiradora" }, tone: "gold", img: U("photo-1653990480360-31a12ce9723e") },
  { id: "watch", credits: 6000, names: { pt: "Smartwatch Premium", en: "Premium Smartwatch", es: "Smartwatch Premium" }, tone: "blue", img: U("photo-1546868871-7041f2a55e12") },
  { id: "fone-premium", credits: 4000, names: { pt: "Fone Premium", en: "Premium Headphones", es: "Audífonos Premium" }, tone: "violet", img: U("photo-1505740420928-5e560c06d30e") },
  { id: "airfryer", credits: 3200, names: { pt: "Air Fryer", en: "Air Fryer", es: "Air Fryer" }, tone: "gold", img: U("photo-1674504866626-fe4f19f68564") },
  { id: "caixa", credits: 2400, names: { pt: "Caixa de Som Bluetooth", en: "Bluetooth Speaker", es: "Bocina Bluetooth" }, tone: "blue", img: U("photo-1608043152269-423dbba4e7e1") },
  { id: "fone-bt", credits: 1600, names: { pt: "Fone Bluetooth", en: "Bluetooth Earbuds", es: "Audífonos Bluetooth" }, tone: "violet", img: U("photo-1606220588913-b3aacb4d2f46") },
];

const FEAT_BADGE = { pt: "Vitrine da temporada", en: "Season showcase", es: "Vitrina de temporada" };
const FEAT_CTA = { pt: "Ver acesso VIP", en: "See VIP access", es: "Ver acceso VIP" };
const BALANCE_LBL = { pt: "Saldo demo", en: "Demo balance", es: "Saldo demo" };
const DEMO_BALANCE = 3450;

function PrizeObject({ tone = "gold", size = "base" }) {
  return (
    <div className={"prize-object prize-object-" + tone + " " + (size === "big" ? "prize-object-big" : "") }>
      <span className="prize-object-face" />
      <span className="prize-object-shine" />
    </div>
  );
}

function ProgressLine({ credits, delay = 0, locale, t }) {
  const pct = Math.min(100, Math.round((DEMO_BALANCE / credits) * 100));
  return (
    <div className="mt-4">
      <div className="mb-2 flex items-center justify-between gap-4">
        <span className="mono text-[10px] font-semibold uppercase tracking-[0.14em] text-white/42">
          {DEMO_BALANCE.toLocaleString(locale)} / {credits.toLocaleString(locale)}
        </span>
        <span className="text-xs font-black text-amber-100">{pct}%</span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-white/10">
        <motion.div
          initial={{ width: 0 }}
          whileInView={{ width: pct + "%" }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
          className="h-full rounded-full bg-[linear-gradient(90deg,#1fa7ff,#ffcf55,#ff7a1a)]"
        />
      </div>
    </div>
  );
}

export default function RewardsStore() {
  const { lang } = useLang();
  const t = STR[lang];
  const locale = lang === "en" ? "en-US" : lang === "es" ? "es-ES" : "pt-BR";
  const price = (c) => c.toLocaleString(locale) + " " + t.credits_word;
  const featured = PRODUCTS[0];
  const rest = PRODUCTS.slice(1);

  return (
    <section id="recompensas" data-testid="rewards-section" className="relative overflow-hidden py-24 sm:py-32">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.22),transparent)]" />
      <div className="pointer-events-none absolute right-[-16%] top-[8%] h-[34rem] w-[34rem] rounded-full bg-[radial-gradient(circle,rgba(255,122,26,0.18),transparent_65%)]" />
      <div className="pointer-events-none absolute left-[-14%] bottom-[8%] h-[32rem] w-[32rem] rounded-full bg-[radial-gradient(circle,rgba(31,167,255,0.18),transparent_65%)]" />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid gap-10 lg:grid-cols-[0.78fr_1.22fr] lg:items-end">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6 }}
              className="mono mb-4 text-xs font-semibold uppercase tracking-[0.22em] text-amber-100/70"
            >
              {t.rew_eyebrow}
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="font-display text-3xl font-black leading-tight tracking-tight text-white sm:text-5xl"
            >
              {t.rew_h2a} <span className="text-[#ffcf68]">{t.rew_h2b}</span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="mt-5 max-w-xl text-base leading-relaxed text-white/62 sm:text-lg"
            >
              {t.rew_sub}
            </motion.p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: 0.18 }}
            data-testid="rewards-demo-balance"
            className="justify-self-start lg:justify-self-end"
          >
            <div className="flex items-center gap-4 rounded-[28px] bg-white/10 px-5 py-4 backdrop-blur-xl">
              <span className="grid h-12 w-12 place-items-center rounded-[18px] bg-[linear-gradient(145deg,#ffcf55,#ff7a1a)] text-[#220900]">
                <Coins className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <div>
                <p className="mono text-[10px] font-semibold uppercase tracking-[0.16em] text-white/45">{BALANCE_LBL[lang]}</p>
                <p className="font-display text-xl font-black leading-tight text-white">
                  {DEMO_BALANCE.toLocaleString(locale)} {t.credits_word}
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        <motion.article
          data-testid="rewards-featured"
          initial={{ opacity: 0, y: 44 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="prize-hero mt-14 grid overflow-hidden rounded-[34px] bg-white/10 p-5 backdrop-blur-2xl sm:p-8 lg:grid-cols-[1.08fr_0.92fr]"
        >
          <div className="relative min-h-[330px] overflow-hidden rounded-[28px] bg-[radial-gradient(circle_at_28%_20%,rgba(255,207,85,0.34),transparent_28%),linear-gradient(145deg,#2b0b61,#0b1554)] sm:min-h-[420px]">
            <span className="spark-star left-[12%] top-[14%]" style={{ "--star-size": "18px" }} />
            <span className="spark-star right-[18%] top-[22%]" style={{ "--star-size": "12px", animationDelay: "0.8s" }} />
            <span className="absolute left-[12%] top-[18%] h-40 w-[76%] -rotate-12 rounded-full border border-white/14" />
            <img
              src={featured.img}
              alt={featured.names[lang]}
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-[linear-gradient(0deg,rgba(11,21,84,0.6),transparent_55%)]" />
            <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between gap-5">
              <div>
                <p className="mono text-[10px] font-semibold uppercase tracking-[0.2em] text-amber-100/78">{FEAT_BADGE[lang]}</p>
                <h3 className="font-display mt-2 text-4xl font-black text-white sm:text-5xl">{featured.names[lang]}</h3>
              </div>
              <Trophy className="hidden h-12 w-12 text-amber-100/75 sm:block" strokeWidth={1.8} />
            </div>
          </div>

          <div className="flex flex-col justify-center px-2 py-8 sm:px-8 lg:py-0">
            <p className="mono text-[10px] font-semibold uppercase tracking-[0.18em] text-white/45">{price(featured.credits)}</p>
            <h3 className="font-display mt-3 max-w-md text-3xl font-black leading-tight text-white sm:text-4xl">
              {lang === "pt" ? "Prêmios grandes, clima de arcade." : lang === "es" ? "Premios grandes, clima arcade." : "Big prizes, arcade energy."}
            </h3>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-white/58 sm:text-base">
              {lang === "pt" ? "A vitrine agora conversa com os jogos: peças brilhantes, cores fortes e menos caixas no caminho." : lang === "es" ? "La vitrina ahora conversa con los juegos: piezas brillantes, colores fuertes y menos cajas." : "The showcase now matches the games: glossy pieces, punchy color, and fewer boxes in the way."}
            </p>
            <ProgressLine credits={featured.credits} delay={0.25} locale={locale} t={t} />
            <button
              onClick={scrollTo("#vip")}
              data-testid="rewards-featured-cta"
              className="game-sheen mt-7 self-start overflow-hidden rounded-[22px] bg-[linear-gradient(135deg,#ffcf55,#ff7a1a)] px-7 py-3.5 font-display text-sm font-black text-[#1a0a02] transition duration-300 hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.98]"
            >
              {FEAT_CTA[lang]}
            </button>
          </div>
        </motion.article>

        <div className="mt-7 grid grid-cols-2 gap-x-5 gap-y-7 lg:grid-cols-4">
          {rest.map((p, i) => (
            <motion.article
              key={p.id}
              data-testid={"reward-card-" + p.id}
              initial={{ opacity: 0, y: 26 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.55, delay: (i % 4) * 0.06, ease: [0.16, 1, 0.3, 1] }}
              whileHover={{ y: -5 }}
              className="group relative"
            >
              <div className="relative mb-4 grid aspect-[1.16] place-items-center overflow-hidden rounded-[28px] bg-white/10 transition duration-300 group-hover:bg-white/15">
                <PrizeObject tone={p.tone} />
                <img
                  src={p.img}
                  alt={p.names[lang]}
                  loading="lazy"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                  className="absolute inset-0 h-full w-full object-cover transition duration-500 group-hover:scale-105"
                />
              </div>
              <h3 className="font-display text-sm font-black leading-snug text-white sm:text-base">{p.names[lang]}</h3>
              <p className="mono mt-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-amber-100/72">{price(p.credits)}</p>
              <ProgressLine credits={p.credits} delay={0.12 + (i % 4) * 0.05} locale={locale} t={t} />
            </motion.article>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mono mt-10 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-white/34"
        >
          <Sparkles className="h-3.5 w-3.5 text-amber-200/70" />
          {t.rew_note}
        </motion.p>
      </div>
    </section>
  );
}
